<!-- src/components/ParticipantMessages.vue -->
<!--
  Per-participant messaging strip, used in the dashboard's expanded
  row directly below each participant card.

  Two states:
    - Collapsed: a thin strip with chevron + 💬 + role label + name +
      message count + last-activity timestamp + red unread badge if
      there are unacknowledged inbound messages.
    - Expanded: shows the SmsThreadPanel underneath, with the full
      conversation.

  Visually connected to the participant card above it (no margin
  between them, shared rounded corners at the join).
-->

<template>
  <div class="participant-messages" :class="{ 'is-expanded': expanded }">

    <!-- Collapsed strip / clickable header -->
    <div class="messages-strip" @click="toggleExpanded">
      <v-icon size="14" class="strip-chevron">
        {{ expanded ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
      </v-icon>
      <v-icon size="16" class="strip-icon">mdi-message-text-outline</v-icon>
      <span class="strip-label">
        Messages with {{ participantDisplayName }}
      </span>
      <span v-if="messageCount > 0" class="strip-count">
        · {{ messageCount }} {{ messageCount === 1 ? 'message' : 'messages' }}
      </span>
      <span v-if="lastTimestamp" class="strip-timestamp">
        · {{ formatTimeAgo(lastTimestamp) }}
      </span>
      <v-spacer />
      <span
        v-if="unreadCount > 0"
        class="strip-unread-badge"
        :title="`${unreadCount} unread`"
      >
        {{ unreadCount }}
      </span>
    </div>

    <!-- Expanded: SMS thread panel -->
    <div v-if="expanded" class="messages-thread-wrap" @click.stop>
      <SmsThreadPanel
        chrome="bare"
        :requires-consent="false"
        :thread="thread"
        :consent="consent"
        :record-language="null"
        :sending="sending"
        ref="smsPanel"
        @send="onSend"
        @send-consent-prompt="onSendConsentPrompt"
        @acknowledge="onAcknowledge"
        @unfurl="onUnfurl"
      />
    </div>

  </div>
</template>


<script>
import SmsThreadPanel from './SmsThreadPanel.vue';

const HOUR = 1000 * 60 * 60;
const DAY = HOUR * 24;

export default {
  name: 'ParticipantMessages',

  components: { SmsThreadPanel },

  props: {
    participant:  { type: Object, required: true },
    role:         { type: String, required: true },  // 'defendant' | 'indemnitor'
    roleSuffix:   { type: String, default: '' },
    expanded:     { type: Boolean, default: false },
    thread:       { type: Array, default: () => [] },
    consent:      { type: Object, default: () => ({ status: 'granted', statusAt: null }) },
    // True while a send is in flight. Passed through to SmsThreadPanel
    // so it can disable the send button. Forms.vue sets this on the
    // active participant's message panel only.
    sending:      { type: Boolean, default: false },
  },

  emits: ['toggle', 'send', 'send-consent-prompt', 'acknowledge', 'unfurl'],

  computed: {
    participantDisplayName() {
      const p = this.participant;
      const name = [p.firstName, p.lastName].filter(Boolean).join(' ');
      return name || (this.role === 'defendant' ? 'defendant' : 'indemnitor' + this.roleSuffix);
    },

    messageCount() {
      // Don't count 'system' entries; those are footer markers, not
      // messages the agent typically thinks of as a "message".
      return this.thread.filter(m => m.type === 'inbound' || m.type === 'outbound').length;
    },

    lastTimestamp() {
      if (this.thread.length === 0) return null;
      const last = this.thread[this.thread.length - 1];
      return last.at;
    },

    unreadCount() {
      return this.thread.filter(
        m => m.type === 'inbound' && m.acknowledged === false
      ).length;
    },
  },

  methods: {
    toggleExpanded() {
      this.$emit('toggle');
    },

    formatTimeAgo(timestamp) {
      if (!timestamp) return '';
      const t = timestamp instanceof Date ? timestamp.getTime() : new Date(timestamp).getTime();
      const diff = Date.now() - t;
      if (diff < 60 * 1000) return 'just now';
      if (diff < HOUR) return `${Math.floor(diff / (60 * 1000))}m ago`;
      if (diff < DAY) return `${Math.floor(diff / HOUR)}h ago`;
      if (diff < 2 * DAY) return 'yesterday';
      return `${Math.floor(diff / DAY)}d ago`;
    },

    onSend(payload)              { this.$emit('send', payload); },
    onSendConsentPrompt(payload) { this.$emit('send-consent-prompt', payload); },
    onAcknowledge()              { this.$emit('acknowledge'); },
    onUnfurl(payload)            { this.$emit('unfurl', payload); },

    // Pass-through called by Forms.vue after a successful send to
    // clear the composer on the wrapped SmsThreadPanel.
    clearComposer() {
      this.$refs.smsPanel?.clearComposer();
    },
  },
};
</script>


<style scoped>
.participant-messages {
  /* Visually attaches to the participant card above. Negative top
     margin pulls the strip right against the card; matching border
     radius on the bottom only. */
  margin-top: -12px;
  margin-bottom: 12px;
  background-color: #fff;
  border: 1px solid #e8e4dc;
  border-top: none;
  border-radius: 0 0 4px 4px;
}

.messages-strip {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  cursor: pointer;
  font-size: 12px;
  color: #666;
  border-top: 1px solid #f0ece4;
  user-select: none;
}
.messages-strip:hover {
  background-color: #faf9f6;
}
.is-expanded .messages-strip {
  background-color: #faf9f6;
}

.strip-chevron {
  color: #999;
}
.strip-icon {
  color: #888;
  margin-right: 2px;
}
.strip-label {
  font-weight: 500;
  color: #555;
}
.strip-count,
.strip-timestamp {
  color: #888;
}

.strip-unread-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: #d32f2f;
  color: white;
  font-weight: 600;
  font-size: 11px;
  min-width: 18px;
  height: 18px;
  border-radius: 9px;
  padding: 0 5px;
  line-height: 1;
}

.messages-thread-wrap {
  padding: 8px 12px 12px 12px;
  border-top: 1px solid #f0ece4;
  background-color: #faf9f6;
}
</style>
